/**
 * DeductionPartnerToHeadPage Component
 * 협력사 → 본사 일차감 페이지
 *
 * 본사가 협력사에게 부과하는 일차감 관리
 * - 조회: DEDUCT_TARGET='S' AND DEDUCT_REG_TYPE='C'
 * - 등록: DEDUCT_TARGET='S', DEDUCT_REG_TYPE='C'
 *
 * 테이블 컬럼: No, 협력사, 공제타입, 공제명, 공제금액, 기간
 */
import React, {useCallback, useEffect, useState} from "react";
import {deductionApi, DeductionDto} from "@/shared/api/deductionApi";
import Loading from '@/shared/components/feedback/Loading';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import SearchableSelect, {SelectOption} from '@/shared/components/forms/SearchableSelect';
import {usePagination} from "@/shared/hooks/usePagination";
import {ToastType} from "@/shared/components/feedback/Toast/Toast.types";
import {useToast} from "@/shared/components/feedback/Toast/ToastContext";
import {formatCurrency} from "@/shared/utils/formatter";
import {getItem} from "@/shared/utils/storage";
import DateInput from "@/shared/components/forms/DateInput";
import {supplierApi} from "@/features/partner/supplierStatus/api/SupplierApi";
import {Supplier} from "@/features/partner/supplierStatus/types/supplier.types";
import {commonCodeApi} from "@/shared/api/commonCodeApi";
import '@/shared/styles/common-table.css';
import '@/shared/styles/search-form.css';

/** 페이지당 아이템 수 */
const ITEMS_PER_PAGE = 10;

/** 새 일차감 등록 폼 데이터 타입 */
interface NewDeductionForm {
    suppId: string;
    deductionTypeCode: string;
    deductionName: string;
    deductionAmount: string;
    startDate: string;
    endDate: string;
    deductDays: string; // 차감 일수 (시작일 포함)
}

const EMPTY_FORM: NewDeductionForm = {
    suppId: '',
    deductionTypeCode: '',
    deductionName: '',
    deductionAmount: '',
    startDate: '',
    endDate: '',
    deductDays: '',
};

/** "20260601" → "2026-06-01" 자동 변환 (8자리 숫자일 때만) */
const normalizeDate = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 8) {
        return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
    }
    return value;
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** 시작일 + 일수 -> 종료일 계산 (시작일 포함, 1일 = 당일만) */
const calcEndDate = (startDate: string, days: number): string => {
    if (!startDate || days <= 0 || !DATE_RE.test(startDate)) return '';
    const d = new Date(startDate);
    if (isNaN(d.getTime())) return '';
    d.setDate(d.getDate() + days - 1);
    return d.toISOString().split('T')[0];
};

/** 시작일 + 종료일 -> 일수 계산 (양 끝 포함) */
const calcDays = (startDate: string, endDate: string): number => {
    if (!DATE_RE.test(startDate) || !DATE_RE.test(endDate)) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
};

const DeductionPartnerToHeadPage: React.FC = () => {
    // Toast
    const { showToast } = useToast();

    // 로딩 상태
    const [loading, setLoading] = useState(false);
    const [suppliersLoading, setSuppliersLoading] = useState(false);
    const [deductionTypesLoading, setDeductionTypesLoading] = useState(false);

    // 데이터
    const [deductions, setDeductions] = useState<DeductionDto[]>([]);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [deductionTypes, setDeductionTypes] = useState<Array<{ code: string; name: string }>>([]);

    // 필터
    const [filterSuppId, setFilterSuppId] = useState<string>('');
    const [newDeduction, setNewDeduction] = useState<NewDeductionForm>(EMPTY_FORM);

    // 페이지네이션
    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({ items: deductions, itemsPerPage: ITEMS_PER_PAGE });

    /**
     * 공제 타입 목록 조회 (vr_comm_code에서 FEE_DEDUCT 그룹)
     */
    const fetchDeductionTypes = useCallback(async () => {
        try {
            setDeductionTypesLoading(true);
            const response = await commonCodeApi.getCommonCodeList('FEE_DEDUCT');
            const types = response.codes
                .filter(code => code.useYn === 'Y')
                .map(code => ({ code: code.comCode, name: code.comCdName }));
            setDeductionTypes(types);
        } catch {
            showToast('공제 타입 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setDeductionTypesLoading(false);
        }
    }, [showToast]);

    /**
     * 협력사 목록 조회
     */
    const fetchSuppliers = useCallback(async () => {
        try {
            setSuppliersLoading(true);
            const response = await supplierApi.getSupplierList();
            setSuppliers(response.suppliers.map(s => ({
                id: s.suppId,
                name: s.suppName || s.suppId,
                status: ''
            })));
        } catch {
            showToast('협력사 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setSuppliersLoading(false);
        }
    }, [showToast]);

    /**
     * 일차감 목록 조회
     * 조건: DEDUCT_TARGET='S' AND DEDUCT_REG_TYPE='C'
     */
    const fetchDeductions = useCallback(async (suppId?: string) => {
        try {
            setLoading(true);

            const response = await deductionApi.getDeductionList({ suppId: suppId || undefined });
            const filtered = (response.deducts || [])
                .filter(d => d.deductTarget === 'S' && d.deductRegType === 'C')
                .sort((a, b) => b.seqNo - a.seqNo);
            setDeductions(filtered);
        } catch {
            showToast('일차감 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setDeductions([]);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    /**
     * 초기 데이터 로드
     */
    useEffect(() => {
        fetchDeductionTypes();
        fetchSuppliers();
        fetchDeductions();
    }, [fetchDeductionTypes, fetchSuppliers, fetchDeductions]);

    /**
     * 조회 버튼 클릭
     */
    const handleSearch = () => {
        fetchDeductions(filterSuppId);
        resetPagination();
    };

    const handleDeleteDeduction = async (seqNo: number) => {
        if (!window.confirm('해당 일차감을 삭제(비활성화)하시겠습니까?')) return;
        try {
            const userId = getItem<string>('USER_ID') || '';
            await deductionApi.toggleUseYn({ seqNos: [seqNo], useYn: 'N', updUser: userId });
            setDeductions(prev => prev.filter(d => d.seqNo !== seqNo));
            showToast('일차감이 삭제되었습니다.', { type: ToastType.SUCCESS });
        } catch {
            showToast('일차감 삭제에 실패했습니다.', { type: ToastType.ERROR });
        }
    };

    /**
     * 새 일차감 등록
     *
     * ── 날짜·일수 연동 핸들러 ────────────────────────────────────────────────
     * 우선순위: 마지막으로 변경한 필드가 나머지를 자동 채움
     *      - startDate 변경 -> deductDays 있으면 endDate 재계산, 없으면 days 재계산
     *      - endDate 변경 -> days 항상 재계산
     *      - deductDays 변경 -> startDate 있으면 endDate 재계산
     */
    const handleStartDateChange = (value: string) => {
        const normalized = normalizeDate(value);
        const days = parseInt(newDeduction.deductDays, 10);
        if (normalized && days > 0) {
            setNewDeduction(prev => ({ ...prev, startDate: normalized, endDate: calcEndDate(normalized, days) }));
        } else if (normalized && newDeduction.endDate) {
            const d = calcDays(normalized, newDeduction.endDate);
            setNewDeduction(prev => ({ ...prev, startDate: normalized, deductDays: d > 0 ? String(d) : '' }));
        } else {
            setNewDeduction(prev => ({ ...prev, startDate: normalized }));
        }
    };

    const handleEndDateChange = (value: string) => {
        const normalized = normalizeDate(value);
        if (normalized && newDeduction.startDate) {
            const d = calcDays(newDeduction.startDate, normalized);
            setNewDeduction(prev => ({ ...prev, endDate: normalized, deductDays: d > 0 ? String(d) : '' }));
        } else {
            setNewDeduction(prev => ({ ...prev, endDate: normalized }));
        }
    };

    const handleDeductDaysChange = (value: string) => {
        const days = parseInt(value, 10);
        if (newDeduction.startDate && days > 0) {
            setNewDeduction(prev => ({ ...prev, deductDays: value, endDate: calcEndDate(newDeduction.startDate, days) }));
        } else {
            setNewDeduction(prev => ({ ...prev, deductDays: value }));
        }
    };

    const handleAddDeduction = async () => {
        // 유효성 검사
        if (!newDeduction.suppId) {
            showToast('협력사를 선택해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!newDeduction.deductionTypeCode) {
            showToast('공제타입을 선택해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!newDeduction.deductionName) {
            showToast('공제명을 입력해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!newDeduction.deductionAmount) {
            showToast('공제금액을 입력해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!newDeduction.startDate) {
            showToast('시작일자를 입력해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!newDeduction.endDate) {
            showToast('종료일자를 입력해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (newDeduction.endDate < newDeduction.startDate) {
            showToast('종료일자는 시작일자 이후여야 합니다.', { type: ToastType.WARNING });
            return;
        }

        try {
            const userId = getItem<string>('USER_ID') || '';
            const deductCnt = calcDays(newDeduction.startDate, newDeduction.endDate);
            const response = await deductionApi.registerDeduction({
                deductTarget: 'S',
                deductRegType: 'C',
                suppId: newDeduction.suppId,
                riderId: null,
                deductType: newDeduction.deductionTypeCode,
                calcType: '1', // 정액
                deductName: newDeduction.deductionName,
                deductCnt: deductCnt > 0 ? deductCnt : null,
                deductAmt: parseInt(newDeduction.deductionAmount, 10),
                deductDispYn: 'Y',
                startDay: newDeduction.startDate,
                endDay: newDeduction.endDate,
                insUser: userId
            });

            showToast('일차감이 등록되었습니다.', { type: ToastType.SUCCESS });

            // 낙관적 업데이트: 새로 등록한 데이터를 즉시 목록 맨 앞에 추가
            const newDeductionData: DeductionDto = {
                seqNo: response.seqNo || Date.now(),
                deductTarget: 'S',
                deductRegType: 'C',
                suppId: newDeduction.suppId,
                riderId: '',
                deductType: newDeduction.deductionTypeCode,
                calcType: '1',
                deductName: newDeduction.deductionName,
                deductCnt: deductCnt > 0 ? deductCnt : null,
                deductAmt: parseInt(newDeduction.deductionAmount, 10),
                deductRate: null,
                deductDispYn: 'Y',
                startDay: newDeduction.startDate,
                endDay: newDeduction.endDate,
                insDate: new Date().toISOString(),
                insUser: userId,
                updDate: null,
                updUser: null
            };

            // 목록 맨 앞에 새 데이터 추가
            setDeductions(prev => [newDeductionData, ...prev]);

            // 페이지네이션 초기화 (첫 페이지로)
            setNewDeduction(EMPTY_FORM);
            resetPagination();
        } catch {
            showToast('일차감 등록에 실패했습니다.', { type: ToastType.ERROR });
        }
    };

    /**
     * 협력사 옵션 변환
     */
    const supplierOptions: SelectOption[] = suppliers.map(s => ({ value: s.id, label: s.name }));

    /**
     * 협력사명 조회
     */
    const getSupplierName = (suppId: string): string =>
        suppliers.find(s => s.id === suppId)?.name ?? suppId;

    /**
     * 공제타입명 조회
     */
    const getDeductionTypeName = (code: string): string =>
        deductionTypes.find(t => t.code === code)?.name ?? code;

    /**
     * 기간 포맷 (시작일 ~ 종료일)
     */
    const formatPeriod = (startDay: string, endDay: string): string => {
        if (!startDay && !endDay) return '-';
        if (!endDay) return `${startDay} ~`;
        if (!startDay) return `~ ${endDay}`;
        return `${startDay} ~ ${endDay}`;
    };

    // 미리보기: 일수·예상 총액
    const previewDays = newDeduction.startDate && newDeduction.endDate
        ? calcDays(newDeduction.startDate, newDeduction.endDate)
        : 0;
    const previewTotal = previewDays > 0 && newDeduction.deductionAmount
        ? previewDays * parseInt(newDeduction.deductionAmount, 10)
        : 0;

    // 로딩 중
    if (loading && deductions.length === 0) {
        return (
            <div className="supplier-selection-page">
                <div className="page-title-wrapper">
                    <h1 className="page-title">협력사 → 본사 일차감</h1>
                </div>
                <Loading message="일차감 내역을 불러오는 중..." />
            </div>
        );
    }

    return (
        <div className="supplier-selection-page">
            {/* 페이지 제목 */}
            <div className="page-title-wrapper">
                <h1 className="page-title">협력사 → 본사 일차감</h1>
            </div>

            {/* 필터 섹션 */}
            <div className="card search-card">
                <div className="card-body">
                    <div className="search-form partner-to-head-search-form">
                        {/* 협력사 필터 */}
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-building" />
                                    협력사
                                </label>
                                <SearchableSelect
                                    options={supplierOptions}
                                    value={filterSuppId}
                                    onChange={setFilterSuppId}
                                    placeholder={suppliersLoading ? '로딩 중...' : '협력사 선택'}
                                    disabled={suppliersLoading}
                                    showAllOption={true}
                                    allOptionLabel="전체"
                                />
                            </div>
                        </div>

                        {/* 조회 버튼 */}
                        <div className="form-group button-group">
                            <button
                                className="btn btn-primary"
                                onClick={handleSearch}
                                disabled={loading}
                            >
                                <i className="fas fa-search" />
                                조회
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 테이블 섹션 */}
            <div className="table-container" style={{ overflowX: 'auto' }}>
                <table className="supplier-selection-table" style={{ minWidth: '1350px', tableLayout: 'auto' }}>
                    <thead>
                    <tr>
                        <th style={{ width: '60px' }}>NO</th>
                        <th style={{ width: '200px' }}>협력사</th>
                        <th style={{ width: '140px' }}>공제타입</th>
                        <th style={{ width: '200px' }}>공제명</th>
                        <th style={{ width: '150px' }}>공제금액</th>
                        <th style={{ width: '490px' }}>기간 / 차감 일수</th>
                        <th style={{ width: '80px' }}>등록</th>
                    </tr>
                    </thead>
                    <tbody>
                    {/* 기존 데이터 행 */}
                    {currentItems.length === 0 && !loading ? (
                        <tr>
                            <td colSpan={7} className="text-center text-gray-600">
                                조회된 일차감 내역이 없습니다.
                            </td>
                        </tr>
                    ) : (
                        currentItems.map((deduction, index) => (
                            <tr key={deduction.seqNo}>
                                <td className="text-center">
                                    {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                                </td>
                                <td>{getSupplierName(deduction.suppId)}</td>
                                <td className="text-center">{getDeductionTypeName(deduction.deductType)}</td>
                                <td>{deduction.deductName}</td>
                                <td className="text-right">{formatCurrency(String(deduction.deductAmt || 0))}원</td>
                                <td className="text-center">
                                    {formatPeriod(deduction.startDay, deduction.endDay)}
                                    {deduction.deductCnt != null && (
                                        <span style={{ color: '#888', marginLeft: '6px', fontSize: '12px' }}>
                                                ({deduction.deductCnt}일)
                                            </span>
                                    )}
                                </td>
                                <td className="text-center">
                                    <button
                                        className="btn btn-sm"
                                        onClick={() => handleDeleteDeduction(deduction.seqNo)}
                                        style={{ color: '#dc3545', fontSize: '12px', padding: '2px 8px', border: '1px solid #dc3545', borderRadius: '4px', background: 'white', cursor: 'pointer' }}
                                    >
                                        삭제
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}

                    {/* 새 일차감 등록 행 */}
                    <tr className="add-row" style={{ backgroundColor: '#f8f9fa' }}>
                        <td className="text-center text-gray-600">+</td>
                        {/* 협력사 선택 */}
                        <td>
                            <SearchableSelect
                                options={supplierOptions}
                                value={newDeduction.suppId}
                                onChange={(value) => setNewDeduction(prev => ({ ...prev, suppId: value }))}
                                placeholder="협력사 선택"
                                disabled={suppliersLoading}
                            />
                        </td>
                        {/* 공제타입 */}
                        <td>
                            <select
                                className="form-select"
                                value={newDeduction.deductionTypeCode}
                                onChange={(e) => {
                                    const code = e.target.value;
                                    const type = deductionTypes.find(t => t.code === code);
                                    setNewDeduction(prev => ({
                                        ...prev,
                                        deductionTypeCode: code,
                                        deductionName: type ? type.name : ''
                                    }));
                                }}
                                disabled={deductionTypesLoading}
                                style={{ width: '100%' }}
                            >
                                <option value="">{deductionTypesLoading ? '로딩 중...' : '선택'}</option>
                                {deductionTypes.map(type => (
                                    <option key={type.code} value={type.code}>{type.name}</option>
                                ))}
                            </select>
                        </td>
                        {/* 공제명 */}
                        <td>
                            <input
                                type="text"
                                className="filter-input"
                                value={newDeduction.deductionName}
                                onChange={(e) => setNewDeduction(prev => ({ ...prev, deductionName: e.target.value }))}
                                placeholder="공제명"
                                style={{ width: '100%' }}
                            />
                        </td>
                        {/* 공제금액 */}
                        <td>
                            <input
                                type="text"
                                className="filter-input"
                                value={formatCurrency(newDeduction.deductionAmount)}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                    setNewDeduction(prev => ({ ...prev, deductionAmount: value }));
                                }}
                                placeholder="금액"
                                style={{ textAlign: 'right' }}
                            />
                        </td>
                        {/* 기간 + 일수 입력 (한 줄) */}
                        <td>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                <DateInput
                                    id="newStartDate"
                                    value={newDeduction.startDate}
                                    onChange={handleStartDateChange}
                                />
                                <span>~</span>
                                <DateInput
                                    id="newEndDate"
                                    value={newDeduction.endDate}
                                    onChange={handleEndDateChange}
                                />
                                <input
                                    type="number"
                                    min="1"
                                    className="filter-input"
                                    value={newDeduction.deductDays}
                                    onChange={(e) => handleDeductDaysChange(e.target.value)}
                                    placeholder="일수"
                                    style={{ width: '70px', textAlign: 'center' }}
                                />
                                <span style={{ fontSize: '12px', color: '#555' }}>일</span>
                                {previewDays > 0 && (
                                    <span style={{ fontSize: '11px', color: '#1a73e8' }}>
                                            {previewTotal > 0
                                                ? `(총 ${formatCurrency(String(previewTotal))}원)`
                                                : `(${previewDays}일)`}
                                        </span>
                                )}
                            </div>
                        </td>
                        {/* 등록 버튼 */}
                        <td className="text-center">
                            <button
                                className="btn btn-primary btn-register"
                                onClick={handleAddDeduction}
                            >
                                <i className="fas fa-plus-circle" />
                            </button>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            {/* 페이지네이션 */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
            />
        </div>
    );
};

export default DeductionPartnerToHeadPage;
